/**
 * THE single source of truth for "what art does tier N of armour slot S use".
 *
 * OWNER (2026-08-17): "I have a heavy helmet now, but it shows the standard
 * helmet in my bag AND in the anvil. Also the plate isn't the correct one. USE
 * WHAT SPRITES ARE APPLIED TO THE HERO to define the correct item."
 *
 * There were three independent answers to that question before this file:
 *   1. the hero's paper-doll (plateFor/helmetFileFor in views/farm/scene.ts),
 *   2. the bag icon (a `${slot}_${metal}.png` name in ui/item-icons.ts, cut by
 *      scripts/build-armor-icons.py from a SECOND copy of the tier→colour table
 *      written in python — so the two tables could drift silently, and the stone
 *      rung already had: the hero wore the plain Iron plate while the bag drew a
 *      desaturated recolour of it),
 *   3. the anvil row, which never had armour art at all — CRAFT_SLOT_ICON has no
 *      helmet/chest/legs key, so renderCrafting fell through to the vector
 *      pictogram in SLOT_ICON. That is why a heavy helm still showed as "the
 *      standard helmet" in the forge no matter how many times the icons were
 *      regenerated.
 *
 * Now there is one: `armourLayerFile(slot, tier)` names the pack layer, the hero
 * wears exactly that file, and the icon URL is that file's BASENAME under
 * /sprites/ui/armor/ (build-armor-icons.py crops every plate layer in the pack
 * and saves it under its own name). Icon and hero cannot disagree — a wrong icon
 * would have to be a wrong hero layer too.
 *
 * This module deliberately imports nothing: scene.ts pulls Pixi in, and the bag
 * and anvil must be able to answer the question without a renderer.
 */

/**
 * Armour tier → the pack colour of the plate the hero wears.
 * (Moved here from views/farm/scene.ts, which now re-exports it.)
 *
 * Copper is the pack's Bronze and mythril its cyan Blue, matching the ore palette
 * in map/ores.ts. Stone has no plate in the pack, so it wears the plainest one —
 * Iron — and therefore shows the Iron plate in the bag and anvil too. It used to
 * show a desaturated fake there, which is precisely the "plate isn't the correct
 * one" mismatch: the panel promised art the character never wore.
 */
export const PLATE_COLOUR = ['Iron', 'Bronze', 'Iron', 'Gold', 'Blue'] as const;

/**
 * The highest armour tier the PACK has a metal for. Above this the plate is
 * painted instead of picked — see `armourIsPainted`.
 */
export const PACK_PLATE_TOP_TIER = PLATE_COLOUR.length;

/**
 * Armour tiers above the pack's palette are PAINTED, not picked.
 *
 * OWNER: "for new ore and bars, i see still mythril armor, that's bad, adapt to
 * each armor, correct armor — green bar → green armor (emerald)."
 *
 * He is describing a clamp. The pack draws four plate metals, so PLATE_COLOUR
 * ends at Mythril's Blue and `plateFor` used to clamp into it — which meant
 * emerald, amber AND ruby armour all wore the cyan mythril plate, in the bag, in
 * the anvil and on the hero. Three tiers of increasingly expensive armour that
 * looked identical to the tier below them.
 *
 * There is no fifth, sixth and seventh plate to slice, so these tiers take the
 * PLAINEST plate — Iron, the least saturated, which is why `plateFor` returns it
 * here rather than clamping to Blue — and get the tier's ore colour applied. The
 * compositor's garment pass is a luminance-preserving remap (see
 * character/compositor.ts), so the plate's own shading survives and an emerald
 * breastplate is the same armour in green, not a green silhouette. It is the same
 * sanction the owner already gave the weapons: "if we don't have sprites, we need
 * to use those colors."
 *
 * The COLOUR itself is not decided here. This module imports nothing on purpose
 * (the bag and anvil must answer without a renderer), so callers resolve it with
 * `armourTint` from map/ores.ts — the one table the veins, bars and tools read.
 */
/**
 * Tier 1 is painted too, and for the same reason as the top three.
 *
 * The pack has no STONE plate either, so PLATE_COLOUR's first entry is Iron — and
 * its third entry is also Iron, because tier 3 genuinely is Iron. Stone armour and
 * Iron armour were therefore the same picture everywhere, so crafting the Iron set
 * changed nothing on screen. It gets the stone colour (map/ores STONE_ARMOUR_TINT)
 * over the same plate.
 */
export const armourIsPainted = (tier: number): boolean => {
  const t = Math.floor(tier);
  return t === 1 || t > PACK_PLATE_TOP_TIER;
};

/** Tier is clamped, never out of range: an undefined colour becomes a 404 layer path. */
export const plateFor = (tier: number): string =>
  armourIsPainted(tier)
    ? 'Iron'
    : PLATE_COLOUR[Math.max(1, Math.min(PACK_PLATE_TOP_TIER, Math.floor(tier))) - 1];

/**
 * The pack ships two helmet sets; the top two rungs (Gold, Mythril) wear the
 * heavy one, so late game is marked by SHAPE and not only by metal.
 */
export const HEAVY_HELM_FROM_TIER = 4;

/** Armour tier → the head layer the hero wears (light vs heavy plate helm). */
export const helmetFileFor = (tier: number): string => {
  const c = plateFor(tier);
  return Math.floor(tier) >= HEAVY_HELM_FROM_TIER
    ? `Head/Plate_Helmet_2/Heavy_Plate_Helmet_1_${c}.png`
    : `Head/Plate_Helmet_1/Plate_Helmet_1_${c}.png`;
};

export const ARMOUR_SLOTS = ['helmet', 'chest', 'legs'] as const;
export type ArmourSlot = (typeof ARMOUR_SLOTS)[number];

/**
 * The pack layer file (relative to /player/) worn on `slot` at `tier`, or null
 * for tier 0 / an unknown slot — nothing equipped means no art anywhere.
 */
export const armourLayerFile = (slot: string, tier: number): string | null => {
  if (!(Math.floor(tier) > 0)) return null;
  const c = plateFor(tier);
  if (slot === 'helmet') return helmetFileFor(tier);
  if (slot === 'chest') return `Chest/Plate_Chest/Plate_Chest_${c}.png`;
  if (slot === 'legs') return `Legs/Plate_Legs/Plate_Legs_${c}.png`;
  return null;
};

/**
 * Icon URL for a worn layer. The basename is unique across the four plate sheets
 * (Plate_Helmet_1_*, Heavy_Plate_Helmet_1_*, Plate_Chest_*, Plate_Legs_*), so the
 * icon keeps the layer's own filename — grep the name and you find both the art
 * the hero wears and the icon the panels draw.
 */
export const layerIconSrc = (layerFile: string): string =>
  `/sprites/ui/armor/${layerFile.slice(layerFile.lastIndexOf('/') + 1)}`;

/** The bag/anvil icon for a slot at a tier — derived from the hero's own layer. */
export const armourIconSrc = (slot: string, tier: number): string | null => {
  const f = armourLayerFile(slot, tier);
  return f ? layerIconSrc(f) : null;
};
